<template>
  <vx-card card-background="dark" id="video-chat-card">
    <moveable class="moveable" v-bind="moveable" @drag="handleDrag" @scale="handleScale" ref="moveable">
      <div>
        <video ref="localVideo" autoplay playsinline muted></video>
      </div>
    </moveable>
    <video ref="remoteVideo" id="video" autoplay playsinline></video>
    <div id="video-control">
      <div
        class="video-chat-button "
        :class="{ 'bg-danger-gradient': !incomingCall, 'bg-success-gradient': incomingCall }"
      >
        <feather-icon icon="PhoneIcon" @click="acceptCall" v-if="incomingCall" :svgClasses="['w-6', 'h-6']" />
        <feather-icon icon="PhoneOffIcon" @click="endCall" v-else :svgClasses="['w-6', 'h-6']" />
      </div>
      <vs-spacer />
      <div class="video-chat-button ml-3 bg-warning-gradient" @click="flipCamera" v-if="!flipBtnDisabled">
        <feather-icon icon="RefreshCwIcon" />
      </div>
      <div
        class="video-chat-button ml-3"
        :class="{ 'bg-primary-gradient': !videoDisabled }"
        @click="videoDisabled = !videoDisabled"
        v-if="!audioDisabled"
      >
        <feather-icon icon="VideoIcon" v-if="!videoDisabled" />
        <feather-icon icon="VideoOffIcon" v-else />
      </div>
      <div
        class="video-chat-button ml-3"
        :class="{ 'bg-primary-gradient': !audioDisabled }"
        @click="audioDisabled = !audioDisabled"
        v-if="!videoDisabled"
      >
        <feather-icon icon="MicIcon" v-if="!audioDisabled" />
        <feather-icon icon="MicOffIcon" v-else />
      </div>
    </div>
    <div id="video-options">
      <div class="vx-row">
        <div class="video-chat-button" @click="showFullscreen">
          <div>
            <i class="fa fa-expand "></i>
          </div>
        </div>
        <vs-spacer />
        <div class="relative flex mr-4 items-center">
          <h6 class="text-white pr-2 user-fullname">{{ user.fullName }}</h6>
          <avatar
            class="m-0 border-2 border-solid border-white"
            size="40px"
            :id="user.avatar"
            @click.stop="$emit('showProfileSidebar', user.id)"
          />
        </div>
      </div>
    </div>
  </vx-card>
</template>

<script>
/* eslint-disable no-console */
import Moveable from 'vue-moveable'
import Avatar from '@/views/components/common/Avatar'
import phoneCalling from '@/assets/phone-calling.mp3'
const callingAudio = new Audio(phoneCalling)
callingAudio.loop = true
import phoneRing from '@/assets/phone-ring.mp3'
const ringAudio = new Audio(phoneRing)
ringAudio.loop = true

const iceServers = [
  {
    url: 'turn:193.176.242.13:3478',
    credential: 'turn30tebsec',
    username: 'guest'
  },
  {
    url: 'turn:37.152.178.74:3478',
    credential: 'turn30tebsec',
    username: 'guest'
  }
]

export default {
  name: 'VideoChat',
  components: {
    Moveable,
    Avatar
  },
  props: {
    user: Object,
    isCaller: Boolean,
    isVideoDisabled: Boolean
  },
  data() {
    return {
      audioSrc: '',
      audioDisabled: false,
      videoSrcIndex: 0,
      videoDevices: [],
      flipBtnDisabled: true,
      videoDisabled: false,
      incomingCall: false,
      moveable: {
        draggable: true,
        throttleDrag: 0,
        resizable: false,
        throttleResize: 1,
        keepRatio: true,
        scalable: true,
        throttleScale: 0,
        rotatable: false,
        throttleRotate: 0,
        pinchable: ['draggable', 'resizable', 'scalable'],
        origin: false
      }
    }
  },
  computed: {
    constraints() {
      return {
        audio: !this.audioDisabled,
        video: this.videoDisabled
          ? false
          : {
              // width: { ideal: 480 },
              // height: { ideal: 640 },
              deviceId: { exact: this.videoDevices[this.videoSrcIndex] },
              frameRate: { ideal: 10, max: 15 }
            }
      }
    }
  },
  watch: {
    videoSrcIndex() {
      this.stopStream()
      this.getStream()
    },
    audioDisabled() {
      this.stopStream()
      this.getStream()
    },
    videoDisabled() {
      this.stopStream()
      this.getStream()
    }
  },
  methods: {
    stopStream() {
      if (this.$refs.localVideo.srcObject)
        this.$refs.localVideo.srcObject.getTracks().forEach(t => {
          t.stop()
        })
    },
    gotDevices(deviceInfos) {
      this.flipBtnDisabled = deviceInfos.length === 1
      this.videoDevices = deviceInfos.filter(d => d.kind === 'videoinput').map(d => d.deviceId)
    },
    flipCamera() {
      this.videoSrcIndex = (this.videoSrcIndex + 1) % this.videoDevices.length
    },
    getStream() {
      if (!this.$route.query.debug) navigator.mediaDevices.getUserMedia(this.constraints).then(this.gotStream)
      // .catch(this.$notifyError)
      else navigator.mediaDevices.getDisplayMedia(this.constraints).then(this.gotStream)
      // .catch(this.$notifyError)
    },
    gotStream(stream) {
      this.$refs.localVideo.srcObject = stream
      window.localConnection.addStream(stream)

      console.log(`[Chat] You called ${this.user.userId}.`)
      if (this.isCaller) callingAudio.play()

      window.localConnection.createOffer(
        offer => {
          console.log(`[Chat] Offer sent to ${this.user.userId}.`)

          this.$io.emit('signalling', {
            to: this.user.userId,
            type: 'offer',
            data: offer
          })

          window.localConnection.setLocalDescription(offer)
        },
        error => {
          console.log('[Chat] Offer sent error:', error)
        }
      )

      let window_width =
        window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth
      // eslint-disable-next-line no-unused-vars
      let window_height =
        window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight

      this.$refs.localVideo.style.height = `${window_height / 3}px`
      this.$refs.localVideo.style.width = `${window_width / 3}px`
    },
    endCall() {
      this.stopStream()
      this.$refs.remoteVideo.srcObject = null
      this.$refs.localVideo.srcObject = null

      window.localConnection.close()
      window.localConnection = new RTCPeerConnection({ iceServers })

      this.$io.emit('signalling', {
        to: this.user.userId,
        type: 'end',
        data: 'call ended'
      })

      this.$emit('call-end', true)
    },
    acceptCall() {
      navigator.mediaDevices
        .enumerateDevices()
        .then(this.gotDevices)
        .then(this.getStream)
        .then(() => {
          this.incomingCall = false
          ringAudio.pause()
        })
      // .catch(this.$notifyError)
    },
    handleDrag({ target, transform }) {
      target.style.transform = transform
    },
    // eslint-disable-next-line no-unused-vars
    handleScale({ target, transform, scale }) {
      // console.log('onScale scale', scale)
      target.style.scale = transform
    },
    showFullscreen() {
      const elem = this.$refs.remoteVideo

      if (elem.requestFullscreen) {
        elem.requestFullscreen()
      } else if (elem.webkitRequestFullscreen) {
        /* Safari */
        elem.webkitRequestFullscreen()
      } else if (elem.msRequestFullscreen) {
        /* IE11 */
        elem.msRequestFullscreen()
      }
    }
  },
  mounted() {
    let supports = navigator.mediaDevices.getSupportedConstraints()
    if (supports['facingMode'] === true) {
      this.flipBtnDisabled = false
    }

    this.videoDisabled = this.isVideoDisabled
    console.log('[Chat] isCaller is? ' + this.isCaller)

    // Setup ice handling
    window.localConnection.onicecandidate = e => {
      if (e.candidate) {
        console.log('[Chat] window.localConnection.onicecandidate() called.')

        this.$io.emit('signalling', {
          to: this.user.userId,
          type: 'candidate',
          data: e.candidate
        })
      }
    }

    window.localConnection.ontrack = e => {
      console.log('[Chat] localConnection.ontrack() called.')
      callingAudio.pause()

      this.$refs.remoteVideo.srcObject = e.streams[0]
    }

    if (this.isCaller) this.acceptCall()
    else {
      this.incomingCall = true
      ringAudio.play()
    }
  },
  beforeDestroy() {
    this.stopStream()
    window.localConnection.close()
    window.localConnection = new RTCPeerConnection({ iceServers })

    this.$io.emit('signalling', {
      to: this.user.userId,
      type: 'end',
      data: 'call ended'
    })
    ringAudio.pause()
    callingAudio.pause()
  }
}
</script>

<style lang="scss">
#video-chat-card {
  z-index: 99999;
  .vx-card__body {
    padding: 0.5rem;
  }
  .user-fullname {
    text-shadow: 0 2px 6px rgba(0, 0, 0, 0.8);
  }
}

#video {
  max-width: 100%;
  max-height: 100%;
  width: 100%;
  height: 100%;
}

.video-chat-button {
  height: 45px;
  width: 45px;
  opacity: 0.8;
  font-size: 20px;
  cursor: pointer;
  background: rgba(100, 100, 100, 0.7);
  color: #f5f5f5;
  border-radius: 45px;
  display: flex;
  justify-content: center;
  align-items: center;
  div {
    display: flex;
    justify-content: center;
    align-items: center;
  }
}

#video-options {
  position: absolute;
  top: 0;
  padding: 2%;
  width: 100%;
  z-index: 9999;

  .v-select .vs__dropdown-toggle {
    background: rgba(240, 240, 240, 0.9);
  }
}

#video-control {
  position: absolute;
  bottom: 0;
  padding: 2%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}
#box-widget-icon {
  // goftino
  display: none !important;
}
.moveable-control-box {
  z-index: 99998 !important;
  display: none !important;
}
.moveable {
  position: absolute;
  z-index: 9998;
  top: 0;
  right: 0;
}
</style>
